import { Box, Grid, IconButton } from '@mui/material'
import ButtonMobileComponent from 'components/button-mobile'
import { DeleteIcon } from 'components/icons'
import InputMobileComponent from 'components/input-mobile'
import { useEffect, useState } from 'react'
import { UpdateSchoolInputProps } from 'types/SchoolTypes'
import { MAX_SCHOOL_ADMINS, MIN_SCHOOL_ADMINS } from 'utils/constants'
import { isValidEmail } from 'utils/validations'
import SettingsBox from './SettingsBox'

interface UserManagementSettingsProps {
  initialAdmins: string[]
  onSave: (body: UpdateSchoolInputProps, loadingMsg?: string, cb?: () => void) => Promise<void>
}

function UserManagementSettings({ initialAdmins, onSave }: UserManagementSettingsProps) {
  const [schoolAdmins, setSchoolAdmins] = useState<string[]>(initialAdmins ?? [])
  const [addSchoolAdmin, setAddSchoolAdmin] = useState<string>('')

  // Sync local state with props when props change
  useEffect(() => {
    setSchoolAdmins(initialAdmins ?? [])
  }, [initialAdmins])

  // hasChanges is always derived
  /* eslint @typescript-eslint/no-var-requires: "off" */
  const isEqual = require('lodash.isequal')
  const hasChanges = !isEqual(schoolAdmins, initialAdmins)

  // Handlers
  const handleDeleteAdmin = (index: number) => {
    if (schoolAdmins.length > MIN_SCHOOL_ADMINS) {
      const updatedAdmins = [...schoolAdmins]
      updatedAdmins.splice(index, 1)
      setSchoolAdmins(updatedAdmins) // Do not apply Delete Immediately
    }
  }

  const handleAdminEmailChange = (index: number, value: string) => {
    const updatedAdmins = [...schoolAdmins]
    updatedAdmins[index] = value
    setSchoolAdmins(updatedAdmins)
  }

  const isInvalidSchoolAdminEmail = (index: number): boolean => {
    return !isValidEmail(schoolAdmins[index])
  }

  const isInvalid: boolean =
    schoolAdmins.some((email) => !isValidEmail(email)) || !!(addSchoolAdmin && !isValidEmail(addSchoolAdmin))

  const handleAdd = async () => {
    if (!isInvalid) {
      const updatedAdmins = [...schoolAdmins, addSchoolAdmin]
      setSchoolAdmins(updatedAdmins)
      await onSave({ schoolAdmins: updatedAdmins })
      setAddSchoolAdmin('')
    }
  }

  const handleSave = async () => {
    if (hasChanges && !isInvalid) {
      await onSave({ schoolAdmins }, 'Updating admin users...')
    }
  }

  return (
    <SettingsBox title='Admin User Management' subtitle='Manage school administrators and their access'>
      <div data-testid='school-admins'>
        {schoolAdmins.map((admin, index) => (
          <Grid container key={index} spacing={2} className='items-center mb-2'>
            <Grid item xs>
              <InputMobileComponent
                value={admin}
                type='email'
                onChange={(v: string) => handleAdminEmailChange(index, v)}
                isError={isInvalidSchoolAdminEmail(index)}
                helperText={
                  isInvalidSchoolAdminEmail(index)
                    ? 'Enter a valid email address'
                    : index === 0
                      ? 'Used as primary contact in app for families to request access codes'
                      : ''
                }
              />
            </Grid>
            {schoolAdmins.length > MIN_SCHOOL_ADMINS && (
              <Grid item xs='auto'>
                <IconButton
                  aria-label='delete'
                  color='error'
                  onClick={() => handleDeleteAdmin(index)}
                  title='Delete Admin'
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            )}
          </Grid>
        ))}
        {schoolAdmins.length < MAX_SCHOOL_ADMINS && (
          <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-start', mb: 1, gap: 1 }}>
            <InputMobileComponent
              placeholder='Add new admin email'
              value={addSchoolAdmin}
              onChange={setAddSchoolAdmin}
            />
            {addSchoolAdmin && (
              <ButtonMobileComponent text='Add' width='w-[50px]' onClick={handleAdd} disabled={isInvalid} />
            )}
          </Box>
        )}
      </div>
      {hasChanges && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <ButtonMobileComponent text='Save' width='w-[100px]' disabled={isInvalid} onClick={handleSave} />
        </Box>
      )}
    </SettingsBox>
  )
}

export default UserManagementSettings
